import { Fragment, useMemo, useState } from 'react';
import { Grid, Group, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Resource } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { ConceptBadge } from '../ConceptBadge';
import { ViewButton } from '@/components/ViewButton';
import { ResourceListSectionConfig } from './ResourceListSectionConfig';
import { ResourceListDisplay } from './ResourceListDisplay';

// -----------------------------------------------------------------------------
type TranslationFunction = (
  key: string,
  options?: { defaultValue?: string }
) => string;

// -----------------------------------------------------------------------------
export interface ResourceListSectionProps<T extends Resource> {
  readonly resources: T[];
  readonly config: ResourceListSectionConfig<T>;
}

// -----------------------------------------------------------------------------
export const ResourceListSection = <T extends Resource>({
  resources: initialResources,
  config,
}: ResourceListSectionProps<T>) => {
  const { t } = useTranslation(['patient-pages']);
  const [resources] = useState<T[]>(initialResources);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedResource, setSelectedResource] = useState<T>();

  // ---------------------------------------------------------------------------
  // Create a typed translation wrapper
  const tWrapped: TranslationFunction = (key, options) => {
    return String(t(key as any, options as any));
  };

  // ---------------------------------------------------------------------------
  // Memoize sorted resources
  const sortedResources = useMemo(
    () => config.sortResources(resources),
    [resources, config]
  );

  // ---------------------------------------------------------------------------
  return (
    <>
      <Group justify="space-between">
        <Text fz="md" fw={700}>
          {tWrapped(config.titleKey, { defaultValue: config.titleKey })}
        </Text>
        <ViewButton
          onClick={() => {
            setSelectedResource(undefined);
            open();
          }}
        />
      </Group>

      {sortedResources.length > 0 ? (
        <Grid gutter="xs">
          {sortedResources.map((resource) => (
            <Fragment key={resource.id}>
              {!config.hideDate && (
                <Grid.Col span={2}>
                  {config.getDisplayDate(resource).substring(0, 4) || '-'}
                </Grid.Col>
              )}
              <Grid.Col span={config.hideDate ? 12 : 10}>
                <ConceptBadge<T>
                  resource={resource}
                  display={config.getDisplayName(resource)}
                  rightSectionElement={
                    config.renderBadgeRightSection ? (
                      config.renderBadgeRightSection(resource)
                    ) : config.getStatusLabel ? (
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ textTransform: 'none' }}
                      >
                        {config.getStatusLabel(resource, tWrapped)}
                      </Text>
                    ) : null
                  }
                  onClick={() => {
                    setSelectedResource(resource);
                    open();
                  }}
                />
              </Grid.Col>
            </Fragment>
          ))}
        </Grid>
      ) : (
        <Text>
          {tWrapped(config.noneKey, { defaultValue: config.noneKey })}
        </Text>
      )}

      <Modal
        opened={opened}
        onClose={close}
        title={tWrapped(config.titleKey, { defaultValue: config.titleKey })}
        centered
      >
        <ResourceListDisplay<T>
          resources={selectedResource ? [selectedResource] : sortedResources}
          config={config}
        />
      </Modal>
    </>
  );
};
